import dayjs, { type Dayjs } from 'dayjs';

import type { WorkItem, WorkKind } from './workflow-api';

/**
 * Pure logic for the work queue (phase 174).
 *
 * Bucketing, grouping, filtering and the snooze arithmetic live here so the
 * page and the dashboard preview cannot disagree about which section a row
 * belongs in, and so "what counts as overdue" is one function with a test.
 */

/* --------------------------------------------------------------- buckets -- */

export type Bucket = 'overdue' | 'today' | 'week' | 'later' | 'undated';

export const BUCKETS: ReadonlyArray<{ key: Bucket; label: string }> = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'later', label: 'Later' },
  { key: 'undated', label: 'No date' },
];

export function bucketOf(dueAt: string | null, now: Dayjs): Bucket {
  if (dueAt === null) return 'undated';
  const due = dayjs(dueAt);
  if (due.isBefore(now)) return 'overdue';
  if (due.isSame(now, 'day')) return 'today';
  if (due.isBefore(now.add(7, 'day').startOf('day'))) return 'week';
  return 'later';
}

/**
 * A review row is aged from when the file arrived, so "overdue" would be
 * nonsense for it. It is "late" only when it has sat unreviewed for two days.
 * Phase 177: a grading row is aged from `submitted_at` for exactly the same
 * reason — an attempt is not past a deadline, it is sitting in a pile.
 */
export const REVIEW_LATE_AFTER_HOURS = 48;

/**
 * The kinds whose `due_at` is an arrival time rather than a deadline.
 *
 * Phase 179b adds `message`: a student's question is a pile, not a deadline —
 * nobody promised a date it could be late against. A `task` is deliberately
 * NOT here; it has a real due date and "Overdue" means it.
 */
export function isAged(row: WorkItem): boolean {
  return row.kind === 'review' || row.kind === 'grading' || row.kind === 'message';
}

export function isLate(row: WorkItem, now: Dayjs): boolean {
  if (row.due_at === null) return false;
  if (isAged(row)) {
    return dayjs(row.due_at).add(REVIEW_LATE_AFTER_HOURS, 'hour').isBefore(now);
  }
  return dayjs(row.due_at).isBefore(now);
}

/* --------------------------------------------------------------- grouping -- */

export type GroupBy = 'due' | 'student' | 'board' | 'kind';

export const GROUP_OPTIONS: ReadonlyArray<{ key: GroupBy; label: string }> = [
  { key: 'due', label: 'Due date' },
  { key: 'student', label: 'Student' },
  { key: 'board', label: 'Board' },
  { key: 'kind', label: 'Kind' },
];

/** Plural, sentence-case names for the eight kinds — chips, group headings and
 *  the active-filter row all say the same word for the same thing. */
export const KIND_LABELS: Readonly<Record<WorkKind, string>> = {
  follow_up: 'Follow-ups',
  checklist: 'Checklist',
  card: 'Cards',
  review: 'To review',
  grading: 'To grade',
  assignment: 'Not handed in',
  message: 'To answer',
  task: 'Asked of students',
};

export interface WorkGroup {
  key: string;
  label: string;
  /** Set on the Overdue bucket so the heading can carry the danger tone. */
  urgent: boolean;
  items: WorkItem[];
}

/**
 * Group an already-filtered list. Insertion order is preserved from the
 * server's due-date sort, so within a student or a board the most urgent row
 * still comes first.
 */
export function groupWork(rows: readonly WorkItem[], by: GroupBy, now: Dayjs): WorkGroup[] {
  if (by === 'due') {
    const buckets = new Map<Bucket, WorkItem[]>(BUCKETS.map((bucket) => [bucket.key, []]));
    for (const row of rows) buckets.get(bucketOf(row.due_at, now))?.push(row);
    return BUCKETS.filter((bucket) => (buckets.get(bucket.key) ?? []).length > 0).map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      urgent: bucket.key === 'overdue',
      items: buckets.get(bucket.key) ?? [],
    }));
  }

  const groups = new Map<string, WorkGroup>();
  for (const row of rows) {
    const key =
      by === 'student' ? row.student_id : by === 'kind' ? row.kind : (row.board_id ?? 'none');
    const label =
      by === 'student'
        ? row.student_name
        : by === 'kind'
          ? KIND_LABELS[row.kind]
          : (row.board_name ?? 'Not on a board');
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, { key, label, urgent: false, items: [row] });
    } else {
      existing.items.push(row);
    }
  }
  // Every group's first row is its most urgent; sort groups by that, so the
  // student with the oldest overdue item is on top.
  return [...groups.values()].sort((a, b) => {
    const da = a.items[0]?.due_at ?? null;
    const db = b.items[0]?.due_at ?? null;
    if (da === null && db === null) return a.label.localeCompare(b.label);
    if (da === null) return 1;
    if (db === null) return -1;
    return dayjs(da).valueOf() - dayjs(db).valueOf();
  });
}

/* ---------------------------------------------------------------- counts -- */

export interface WorkSummary {
  overdue: number;
  today: number;
  toAnswer: number;
  toReview: number;
  toGrade: number;
  waitingOnStudent: number;
}

/**
 * The numbers above the list. Aged kinds (a file to review, an attempt to
 * grade, a question to answer) are counted as themselves rather than folded
 * into overdue/today — their `due_at` is an arrival time, so bucketing them by
 * it would report a file that landed this morning as "due today", which is not
 * a deadline anyone agreed to.
 *
 * Phase 179b: `message` joins them. It was added to `isAged` first, and the
 * two must move together — a message left out of this branch would inflate
 * "overdue", the number staff triage on, while `describeDue` on the very same
 * row correctly refused to call it overdue.
 */
export function summariseWork(rows: readonly WorkItem[], now: Dayjs): WorkSummary {
  let overdue = 0;
  let today = 0;
  let toAnswer = 0;
  let toReview = 0;
  let toGrade = 0;
  let waitingOnStudent = 0;
  for (const row of rows) {
    if (row.kind === 'review') {
      toReview += 1;
      continue;
    }
    if (row.kind === 'grading') {
      toGrade += 1;
      continue;
    }
    if (row.kind === 'message') {
      toAnswer += 1;
      continue;
    }
    const bucket = bucketOf(row.due_at, now);
    if (bucket === 'overdue') overdue += 1;
    if (bucket === 'today') today += 1;
    if (row.waiting_on_student) waitingOnStudent += 1;
  }
  return { overdue, today, toAnswer, toReview, toGrade, waitingOnStudent };
}

/* ---------------------------------------------------------------- snooze -- */

export type SnoozePreset = 'tomorrow' | 'three_days' | 'next_week';

export const SNOOZE_PRESETS: ReadonlyArray<{ key: SnoozePreset; label: string }> = [
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'three_days', label: 'In 3 days' },
  { key: 'next_week', label: 'Next week' },
];

/** End of the chosen day, so a snoozed item is never "overdue" at 9am. */
export function snoozeTarget(preset: SnoozePreset, now: Dayjs): string {
  const days = preset === 'tomorrow' ? 1 : preset === 'three_days' ? 3 : 7;
  return now.add(days, 'day').endOf('day').toISOString();
}

/**
 * A picked calendar date (`YYYY-MM-DD`) as the same end-of-day instant.
 *
 * Shape-checked by hand: dayjs's strict parsing lives in a plugin this
 * codebase deliberately does not load (see notifications/relative-time.ts),
 * and without it `dayjs('10/09/2026')` cheerfully guesses.
 */
export function snoozeToDate(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const parsed = dayjs(date);
  if (!parsed.isValid()) return null;
  return parsed.endOf('day').toISOString();
}

/**
 * Which rows can be snoozed or ticked from the queue.
 *
 * Only the three kinds with a date of their own can be snoozed. A review and a
 * grading row are aged from arrival — there is no date to move. An assignment
 * row's date belongs to the assignment, and pushing it from one student's row
 * would silently move the deadline for everyone else on it.
 */
export function canSnooze(row: WorkItem): boolean {
  return row.kind === 'follow_up' || row.kind === 'card' || row.kind === 'checklist';
}

export function canTick(row: WorkItem): boolean {
  return row.kind === 'follow_up' || row.kind === 'checklist' || row.kind === 'task';
}

/**
 * Which rows carry an owner control. An assignment row is one student's line
 * on someone else's assignment: there is no per-student owner to write, and
 * the RPC rejects it.
 */
export function canOwn(row: WorkItem): boolean {
  if (row.kind === 'assignment') return false;
  // Phase 179b: a task's "owner" is whoever asked for it — reassigning that
  // would rewrite history, not delegate work. A `message` DOES own its
  // assignee (student_threads.assigned_to), and institute_assign_work_item
  // gained a branch for it, so "take it" works from the queue.
  if (row.kind === 'task') return false;
  // A review raised from a request can be owned (the request's `requested_by`);
  // an unprompted upload has nothing to hang an owner on.
  if (row.kind === 'review') return row.request_id !== null;
  return true;
}

/** The id `institute_assign_work_item` expects for this row's kind. */
export function assignTargetId(row: WorkItem): string {
  if (row.kind === 'review') return row.request_id ?? row.id;
  if (row.kind === 'grading') return row.attempt_id ?? row.id;
  return row.id;
}

/* ----------------------------------------------------------------- links -- */

/** Where a row opens: the card page, or the dossier tab that owns the thing. */
export function workLink(row: WorkItem): string {
  if (
    (row.kind === 'card' || row.kind === 'checklist') &&
    row.board_id !== null &&
    row.card_id !== null
  ) {
    return `/institute/workflow/${row.board_id}/card/${row.card_id}`;
  }
  if (row.kind === 'review') {
    return `/institute/students/${row.student_id}/dossier?tab=documents`;
  }
  if (row.kind === 'grading' && row.attempt_id !== null) {
    return `/institute/grading/${row.attempt_id}`;
  }
  if (row.kind === 'assignment' && row.assignment_id !== null) {
    return `/institute/assignments/${row.assignment_id}`;
  }
  // Phase 179b: the row's own id IS the thread id / task id, which is why
  // neither kind needed a new column on a union that already has thirty-one.
  if (row.kind === 'message') {
    return `/institute/messages/${row.id}`;
  }
  // A `task` falls through to the dossier, which is where its list lives.
  return `/institute/students/${row.student_id}/dossier?tab=activity`;
}

/** The grey context line — the same idea for a card and a checklist item,
 * carried in different columns because a card's own title already IS the
 * stage name. */
export function workContext(row: WorkItem): string | null {
  if (row.kind === 'card') return row.board_name;
  if (row.kind === 'checklist') return row.subtitle;
  if (row.kind === 'review') return row.subtitle;
  if (row.kind === 'grading') return row.subtitle;
  if (row.kind === 'assignment') return row.subtitle;
  if (row.kind === 'message') return row.subtitle;
  if (row.kind === 'task') return row.subtitle;
  return null;
}

/**
 * "Sent 27 Aug" for a review, "Submitted 27 Aug" for an attempt, "Due 8 Sep" /
 * "Overdue 8 Sep" for anything with a real deadline. The aged kinds never say
 * "Overdue", because nobody promised a date they could be late against.
 */
export function describeDue(row: WorkItem, now: Dayjs): string {
  if (row.due_at === null) return 'No date';
  const date = dayjs(row.due_at).format('D MMM');
  if (row.kind === 'review') return `Sent ${date}`;
  if (row.kind === 'grading') return `Submitted ${date}`;
  if (row.kind === 'message') return `Asked ${date}`;
  return isLate(row, now) ? `Overdue ${date}` : `Due ${date}`;
}
